/**
 * Table support for the notebook editor (FRIDAY-66).
 *
 * `@tiptap/extension-table` v2 ships the table node; the row / cell /
 * header nodes live in their own packages and are extended here.
 *
 * First version (deliberately small):
 *   - one header row, everything below it is a body row;
 *   - a cell holds exactly one paragraph of inline content;
 *   - no merged cells: `colspan` / `rowspan` are fixed at 1, are never
 *     read from the DOM and no merge / split command is exposed;
 *   - no nested tables: a cell can only contain a paragraph;
 *   - no column resizing.
 *
 * The markdown side (GFM pipe tables) lives in `markdownTable.ts`.
 */

import { type CommandProps, type Editor } from '@tiptap/core'
import Table, { createTable } from '@tiptap/extension-table'
import { TableCell as TableCellBase } from '@tiptap/extension-table-cell'
import { TableHeader as TableHeaderBase } from '@tiptap/extension-table-header'
import { TableRow as TableRowBase } from '@tiptap/extension-table-row'
import type { Node as ProseMirrorNode } from '@tiptap/pm/model'
import type { EditorState } from '@tiptap/pm/state'
import { TextSelection } from '@tiptap/pm/state'
import { TABLE_COLS, TABLE_ROWS, tableMarkdownSpec } from './markdownTable'

export const TableRow = TableRowBase

/**
 * A cell holds exactly one paragraph: GFM table cells are single-line, so
 * a second paragraph could not be stored without losing content.
 */
export const TableCell = TableCellBase.extend({
  content: 'paragraph',

  addAttributes() {
    return {
      ...this.parent?.(),
      // Merged cells are out of scope: the span stays at 1 and is not
      // advertised in the DOM.
      colspan: { default: 1, renderHTML: () => ({}) },
      rowspan: { default: 1, renderHTML: () => ({}) },
      colwidth: { default: null, renderHTML: () => ({}) },
    }
  },
})

export const TableHeader = TableHeaderBase.extend({
  content: 'paragraph',

  addAttributes() {
    return {
      ...this.parent?.(),
      colspan: { default: 1, renderHTML: () => ({}) },
      rowspan: { default: 1, renderHTML: () => ({}) },
      colwidth: { default: null, renderHTML: () => ({}) },
    }
  },
})

export interface InsertTableOptions {
  /** Rows, header row included. */
  rows?: number
  /** Columns. */
  cols?: number
  /** Whether the first row is a header row. */
  withHeaderRow?: boolean
  /** Range replaced by the table. Defaults to the current selection. */
  range?: { from: number; to: number }
  /** Text placed in the first cell. Defaults to the replaced range's text. */
  text?: string
}

/**
 * Position of the first character of the first cell, relative to the
 * position of the table node: table -> row -> cell -> paragraph -> text.
 */
const FIRST_CELL_TEXT_OFFSET = 4

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    notebookTable: {
      insertTableWithText: (options?: InsertTableOptions) => ReturnType
    }
  }
}

/**
 * The table node with Friday's markdown serializer, the scrollable wrapper
 * and the `insertTableWithText` command.
 */
export const NotebookTable = Table.extend({
  addOptions() {
    return {
      ...this.parent?.(),
      // No column resizing in the first version.
      resizable: false,
      // The wrapper is what makes a wide table scroll inside the editor
      // instead of widening the page.
      renderWrapper: true,
      cellMinWidth: 120,
      HTMLAttributes: { class: 'friday-table' },
    }
  },

  addStorage() {
    return {
      markdown: tableMarkdownSpec,
    }
  },

  addCommands() {
    return {
      ...this.parent?.(),
      insertTableWithText:
        (options: InsertTableOptions = {}) =>
        ({ state, dispatch }: CommandProps): boolean => {
          const { rows = TABLE_ROWS, cols = TABLE_COLS, withHeaderRow = true } = options
          const from = options.range?.from ?? state.selection.from
          const to = options.range?.to ?? state.selection.to
          // A table cannot be nested: a cell only holds a single paragraph,
          // so the schema would reject the insertion.
          if (isInsideTable(state)) return false

          const text = options.text ?? state.doc.textBetween(from, to, ' ')
          const table = buildTableNode(state.schema, rows, cols, withHeaderRow, text)
          if (!dispatch) return true

          const tr = state.tr.replaceRangeWith(from, to, table)
          const tablePos = findTablePos(tr.doc, tr.mapping.map(from, -1))
          if (tablePos >= 0) {
            tr.setSelection(TextSelection.create(tr.doc, tablePos + FIRST_CELL_TEXT_OFFSET + text.length))
          }
          dispatch(tr)
          return true
        },
    }
  },
})

/** True when the current selection sits inside a table. */
export function isInsideTable(state: EditorState): boolean {
  const { $from } = state.selection
  for (let depth = $from.depth; depth > 0; depth -= 1) {
    if ($from.node(depth).type.name === 'table') return true
  }
  return false
}

/**
 * Turns the current selection (or the block the cursor is in) into a
 * table. The selected text is kept — it becomes the content of the first
 * cell instead of being thrown away.
 */
export function turnIntoTable(editor: Editor, options: Partial<InsertTableOptions> = {}): void {
  const { state } = editor
  const { selection } = state
  let range: { from: number; to: number } | undefined
  let text: string | undefined

  if (!selection.empty) {
    range = { from: selection.from, to: selection.to }
    text = state.doc.textBetween(selection.from, selection.to, ' ')
  } else {
    const { $from } = selection
    const depth = $from.depth >= 1 ? 1 : 0
    const block = $from.node(depth)
    if (block.type.name === 'paragraph' || block.type.name === 'heading') {
      range = { from: $from.before(depth), to: $from.after(depth) }
      text = block.textContent
    }
  }

  editor.chain().focus().insertTableWithText({ ...options, range, text }).run()
}

function buildTableNode(
  schema: EditorState['schema'],
  rows: number,
  cols: number,
  withHeaderRow: boolean,
  text: string,
): ProseMirrorNode {
  const table = createTable(schema, rows, cols, withHeaderRow)
  if (!text) return table

  const firstRow = table.child(0)
  const firstCell = firstRow.child(0)
  const paragraph = schema.nodes.paragraph.create(null, schema.text(text))
  const cell = firstCell.type.create(firstCell.attrs, paragraph, firstCell.marks)

  const content: ProseMirrorNode[] = []
  table.forEach((row, _offset, index) => {
    content.push(index === 0 ? row.type.create(row.attrs, cell, row.marks) : row)
  })
  return table.type.create(table.attrs, content, table.marks)
}

/** Position of the table closest to `near` (the one we just inserted). */
function findTablePos(doc: ProseMirrorNode, near: number): number {
  let best = -1
  doc.descendants((node, pos) => {
    if (node.type.name !== 'table') return true
    if (best === -1 || Math.abs(pos - near) < Math.abs(best - near)) best = pos
    return false
  })
  return best
}
